import React, { useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import styled from 'styled-components';

import { AlertWarning } from '@ad-scripts/presentation/components/common/alert';
import { Body, Row } from 'src/presentation/components/forms/Layout';
import { businessHoursConfigurationDefaults } from 'src/models/BusinessHours/BusinessHours';
import BusinessHoursExceptions from './components/Exceptions/BusinessHoursExceptions';
import businessHoursResource from 'src/actions/businessHours/businessHoursResource';
import { Button, DropdownMenu } from 'src/presentation/components/agent_desktop_components';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import { getAdminURL } from '../shared/utils/Utils';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import Header, { StyledDescription } from './components/Header';
import InputField from 'src/presentation/components/forms/InputField';
import { Section } from 'src/presentation/components/forms/Layout';
import { StyledExternalLink } from '../shared/ExternalLink';
import { TIMEZONE_OPTIONS } from 'src/presentation/components/pages/shared/constants/dateTimeConstants';
import { urlHashToObj } from 'src/presentation/components/pages/ivr/helpers/ivrHelpers';
import useDirtyFormConfirmation from '../shared/hooks/useDirtyFormConfirmation';
import { useResourceValues } from 'src/presentation/hooks/useResource';
import useRouter from 'src/presentation/hooks/useRouter';
import WorkingHours from 'src/presentation/components/pages/shared/workingHours/WorkingHours';

const BusinessHoursEditor = ({ errors, item, root }: FormEditorProps): JSX.Element => {
  const { values: businessHoursItems } = useResourceValues(businessHoursResource);
  const { add, update, validate } = businessHoursResource.effects;
  const { resetErrors } = businessHoursResource.actions;
  const { history } = useRouter();
  const existedItemId = get(urlHashToObj(), 'id');
  const [isComplete, setComplete] = useState<boolean>();
  let isBeingDuplicated = false;

  const getDefaultItem = () => {
    // creating a new record by copying from an existing one
    if (existedItemId) {
      const matchedItem = businessHoursItems.find(item => item.id === existedItemId);

      if (!matchedItem) {
        return history.push(root);
      }

      const clonedItem = cloneDeep(matchedItem);
      clonedItem.name = clonedItem.name ? `${clonedItem.name} (copy)` : '';
      delete clonedItem.id;

      // only one business hours can be primary
      clonedItem.primary = false;

      isBeingDuplicated = true; // to enable Save button if business hours are being duplicated
      return clonedItem;
    }

    return businessHoursConfigurationDefaults;
  };

  const formContext = useForm({
    defaultItem: getDefaultItem(),
    item,
    errors,
    resetErrors,
    root,
    add,
    update,
    validate,
  });

  const { isNew, onClose, onSave } = formContext;

  const header = isNew ? 'Create Business Hours' : `Edit ${item.name}`;

  const { isModified } = useDirtyFormConfirmation({
    formContext,
    isComplete,
    setComplete,
  });

  return (
    <Form context={formContext}>
      <PrimaryHeader data-aid="business-hours-editor-title">{header}</PrimaryHeader>

      <StyledBody data-aid="business-hours-editor">
        {item && item.primary && (
          <StyledAlertWarning>
            <b>Note:&nbsp;</b>
            This is the <b>Primary Business Hours</b> used to assess your SLA performance. To select the Primary
            Business Hours for your SLA go to{' '}
            <StyledExternalLink
              data-aid="sla-business-hours-link"
              to={getAdminURL('sla-business-hours')}
              showIcon={true}
              label="SLA Business Hours"
            />
          </StyledAlertWarning>
        )}
        <Row>
          <InputField
            attr="name"
            data-aid="name"
            label="Name"
            placeholder="i.e. chat business hours, weekly schedule, etc."
          />
        </Row>
        <Row>
          <TimeZoneDropdown
            attr="officeHours.timezone"
            className="timezoneDropdown"
            data-aid="officeHours-timezone"
            fitOpenerWidth={false}
            label="Timezone"
            options={TIMEZONE_OPTIONS}
            placeholder="Select a timezone"
            searchable
          />
        </Row>
        <StyledSection>
          <Header headerText="Business Hours" />
          <StyledDescription>
            Set the days and time range when your business will be open. By setting the schedule, you will be able to
            automate certain actions through Answers and Rules in Gladly.
          </StyledDescription>
          <WorkingHours attr="officeHours" dataAid="officeHours" />
        </StyledSection>
        <Row>
          <BusinessHoursExceptions errors={errors} />
        </Row>
      </StyledBody>

      <Footer>
        <Button data-aid="editor-cancel" type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid="editor-save" onClick={onSave} disabled={!isModified && !isBeingDuplicated}>
          Save
        </Button>
      </Footer>
    </Form>
  );
};

const PrimaryHeader = styled(H1)``;

const TimeZoneDropdown = styled(Dropdown)`
  ${DropdownMenu} {
    border-color: ${p => p.theme.colors.gray300};
    width: auto;
    &:focus {
      border: 1px solid ${p => p.theme.colors.gray600};
    }
  }
`;

const StyledAlertWarning = styled(AlertWarning)`
  margin-bottom: 12px;
  line-height: 20px;
`;

const StyledBody = styled(Body)`
  padding: 0 0 24px 0;
  max-width: 520px;
`;

const StyledSection = styled(Section)`
  > div:first-child {
    // adjustment for the section with no description
    margin-bottom: 0;
  }
`;

export default BusinessHoursEditor;
